/**
 * One option in a segmented control (document type, visibility).
 *
 * It lives at module scope on purpose. It used to be declared INSIDE
 * FileCreate's body, which makes a brand-new component type on every render —
 * React then unmounts and remounts the whole subtree rather than updating it,
 * so any focus or transition inside it is lost on each keystroke elsewhere in
 * the modal. Nothing here closes over the parent, so there was never a reason
 * for it to be nested.
 *
 * A disabled option explains itself through a tooltip: the wrapper div exists
 * because a disabled button fires no pointer events, so the trigger has to sit
 * on something that does.
 */

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export function ToggleButton({ active, onClick, icon: Icon, label, disabled, disabledTooltip }) {
  const button = (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        'flex items-center justify-center gap-2 px-2 py-1.5 text-xs font-medium rounded transition-all w-full h-8 truncate',
        active
          ? 'bg-background text-foreground shadow-sm ring-1 ring-border'
          : disabled
            ? 'text-muted-foreground/50 opacity-50 cursor-not-allowed'
            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50 cursor-pointer',
      )}
    >
      {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}
      <span className="truncate">{label}</span>
    </button>
  );

  if (!disabled || !disabledTooltip) return button;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <div className="w-full">{button}</div>
        </TooltipTrigger>
        <TooltipContent
          className="bg-popover text-popover-foreground text-[11px] rounded-[4px] px-2 py-1.5 shadow-md border-border max-w-[200px] break-words text-center"
          side="top"
        >
          {disabledTooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
